/**
 * React Query хуки поверх контрактов. Ключи кэша централизованы в `keys`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DailyCardResponse, DecksResponse, InventoryResponse, Me, PullResponse, Reading, ShopResponse, TableSlots } from '@tarot/contracts';
import { http } from './client';

export const keys = {
  me: ['me'] as const,
  daily: ['daily'] as const,
  shop: ['shop'] as const,
  inventory: ['inventory'] as const,
  decks: ['decks'] as const,
  readings: ['readings'] as const,
  reading: (id: string) => ['reading', id] as const,
};

export const useMe = (enabled = true) => useQuery({ queryKey: keys.me, queryFn: () => http.get<Me>('/me'), staleTime: 10_000, enabled });
export const useDaily = () => useQuery({ queryKey: keys.daily, queryFn: () => http.get<DailyCardResponse>('/daily-card') });
export const useShop = () => useQuery({ queryKey: keys.shop, queryFn: () => http.get<ShopResponse>('/shop'), staleTime: 30_000 });
export const useInventory = () => useQuery({ queryKey: keys.inventory, queryFn: () => http.get<InventoryResponse>('/inventory'), staleTime: 30_000 });
export const useDecks = () => useQuery({ queryKey: keys.decks, queryFn: () => http.get<DecksResponse>('/decks'), staleTime: 60_000 });
export const useReadings = () => useQuery({ queryKey: keys.readings, queryFn: () => http.get<{ items: Reading[] }>('/readings') });
export const useReading = (id: string | undefined) => useQuery({ queryKey: keys.reading(id ?? ''), queryFn: () => http.get<Reading>(`/readings/${id}`), enabled: !!id });

/** Обновление кэша /me из ответов, которые уже содержат user (экономим запрос). */
export function useSetMe() {
  const qc = useQueryClient();
  return (me: Me) => qc.setQueryData(keys.me, me);
}

export function useInvalidate() {
  const qc = useQueryClient();
  return (...k: (readonly unknown[])[]) => Promise.all(k.map((key) => qc.invalidateQueries({ queryKey: key })));
}

export function useClaimReward() {
  const setMe = useSetMe();
  return useMutation({
    mutationFn: () => http.post<{ reward: number | 'ticket'; dayIndex: number; user: Me }>('/rewards/daily/claim'),
    onSuccess: (r) => setMe(r.user),
  });
}

export function useSetPreset() {
  const setMe = useSetMe();
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (body: { slots?: TableSlots; deckId?: string }) => http.put<{ user: Me }>('/table-preset', body),
    onSuccess: (r) => { setMe(r.user); void inv(keys.daily); },
  });
}

export function usePull() {
  const setMe = useSetMe();
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (body: { bannerId: string; count: 1 | 10; useTicket?: boolean }) => http.post<PullResponse>('/gacha/pull', body),
    onSuccess: (r) => { setMe(r.user); void inv(keys.shop, keys.inventory, keys.decks); },
  });
}

export function useBuy() {
  const setMe = useSetMe();
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (body: { kind: 'item' | 'deck' | 'product'; id: string }) => http.post<{ invoiceUrl: string } | { ok: true; user: Me }>('/shop/buy', body),
    onSuccess: (r) => { if ('user' in r) { setMe(r.user); void inv(keys.shop, keys.inventory, keys.decks); } },
  });
}
